import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  EMPTY,
  Observable,
  concatMap,
  defer,
  finalize,
  from,
  map,
  tap,
  throwError
} from 'rxjs';

import {
  EntityModificationAction,
  EntityModificationOption
} from '@core/models/entities/entity-visualization-interface';
import { CreateOrUpdateGlobalEntityRequest } from '@core/models/entity/entity-selector-request.model';
import {
  DataTransformerSuccessResponse,
  TransformerRequestVersion
} from '@core/models/transformers.model';
import { PlansService } from '@core/services/plans/plans.service';
import { SelectorsService } from '@core/services/selectors/selectors.service';
import { entityModified } from '@core/store/data-visualization/data-visualization.actions';
import {
  selectAvailableTransformers,
  selectDataspaceState
} from '@core/store/dataspace/dataspace.selectors';
import { selectPlan } from '@core/store/plan/plan.selectors';
import { extractPlanIdFromUrl } from '@core/utils/dataspace-url.utils';
import { chunk } from '@core/utils/shared-utils';

/** The batch route rejects a `data` array longer than this. */
export const MAX_BATCH_PATCH_ITEMS = 100;

/**
 * Ceiling on the entity count of one bulk modification. A set wider than the batch route's own
 * cap is split into several requests, so this is what keeps a selection spanning a whole plan
 * from firing hundreds of them.
 */
export const MAX_BULK_PATCH_ITEMS = 500;

/**
 * The record a plan create wrote, or undefined when the response names none.
 *
 * A plan create also pulls the global rows it references into the plan, so the response can
 * name several entity types; the caller wrote the one it asked for, and reporting a pulled
 * row's uid instead would send it to the wrong record.
 */
function persistedUidOf(
  response: DataTransformerSuccessResponse | null,
  entityType: string
): { uid: string } | undefined {
  const persisted = response?.persistedData ?? [];
  const written =
    persisted.find(
      (entry) => entry.entityName?.toUpperCase() === entityType.toUpperCase()
    ) ?? persisted[0];
  const uid = written?.entityIds?.[0]?.uid;
  return uid === undefined ? undefined : { uid };
}

@Injectable({ providedIn: 'root' })
export class EntityModificationService {
  private readonly selectorsService = inject(SelectorsService);
  private readonly plansService = inject(PlansService);
  private readonly store = inject(Store);

  private readonly router = inject(Router);

  private readonly allTransformers = this.store.selectSignal(selectAvailableTransformers);
  private readonly dataspaceState = this.store.selectSignal(selectDataspaceState);
  private readonly storePlan = this.store.selectSignal(selectPlan);

  /**
   * The plan being written to. The URL is asked first: the store's plan is fetched after
   * navigation, so a write submitted in the window before it lands would read as "no plan"
   * and go to global data instead — silently, since both routes succeed.
   *
   * The store still answers for the windows whose route carries no plan at all. The data
   * visualization popup is its own top-level route, `/data-visualization`, yet edits made
   * there (a dragged calendar event, a committed geo-area) are saved by the popup itself;
   * the plan reaches it through store replication, so without this fallback they would all
   * land in global data. Consulting the store cannot bring back the race it replaced: inside
   * a plan the URL always carries the id, and elsewhere `RouteStateSyncService` clears the
   * plan, so the fallback yields null exactly where global data is the right target.
   */
  private routePlanId(): string | null {
    return extractPlanIdFromUrl(this.router.url) ?? this.storePlan()?.id ?? null;
  }

  /**
   * Primitive plan-aware PUT. Routes to PlansService or SelectorsService according to whether
   * the URL is inside a plan. Strips dataSpaceId from the request body when routing through
   * the plan endpoint.
   */
  planAwareUpdate$(
    entityType: string,
    request: CreateOrUpdateGlobalEntityRequest
  ): Observable<void> {
    const planId = this.routePlanId();
    if (planId) {
      const { dataSpaceId: _, ...planRequest } = request;
      return this.plansService.updateEntityData$(planId, entityType, planRequest);
    }
    if (!request.dataSpaceId) return EMPTY;
    return this.selectorsService.updateEntityData$(entityType, request);
  }

  /**
   * Primitive plan-aware POST, the mirror of `planAwareUpdate$`. Inside a plan the record is
   * created in that plan and nowhere else; global data is untouched.
   *
   * Both routes report the uid back — the caller may not know it, since a create can leave it
   * blank and let the backend mint one — so the plan response is narrowed to the same shape
   * the global route already returns.
   */
  planAwareCreate$(
    entityType: string,
    request: CreateOrUpdateGlobalEntityRequest
  ): Observable<{ uid?: string } | undefined> {
    const planId = this.routePlanId();
    if (planId) {
      const { dataSpaceId: _, ...planRequest } = request;
      return this.plansService
        .createEntityData$(planId, entityType, planRequest)
        .pipe(map((response) => persistedUidOf(response, entityType)));
    }
    if (!request.dataSpaceId) return EMPTY;
    return this.selectorsService.createEntityData$(entityType, request);
  }

  /**
   * Looks up the transformer for the given entityTypeVersion, then delegates to planAwareUpdate$.
   * Use when the caller does not have a pre-resolved transformer.
   */
  saveEntityData$(
    entityType: string,
    entityTypeVersion: string,
    data: () => Record<string, unknown>
  ): Observable<void> {
    const transformer = this.transformerFor(entityTypeVersion);
    if (!transformer) return EMPTY;

    return this.planAwareUpdate$(entityType, {
      data: data(),
      transformer,
      dataSpaceId: this.dataspaceState().dataspace?.id,
      entityTypeVersion
    });
  }

  /**
   * Merges the same fields into every listed entity, and announces the modification once for
   * the set.
   *
   * The batch route takes at most `MAX_BATCH_PATCH_ITEMS` uids, so a wider set goes out as
   * consecutive requests: each is all-or-none on its own, and every emission is how many
   * entities have been written so far — what a caller needs to report a write that stopped
   * part-way.
   *
   * Plan-scoped only: the batch route lives under `plans/{id}/data`, so a caller outside a
   * plan is a programming error rather than a case to route elsewhere.
   */
  applyBatchModification$(
    entityType: string,
    entityTypeVersion: string,
    uids: string[],
    fields: Record<string, unknown>
  ): Observable<number> {
    const planId = this.routePlanId();
    if (!planId) {
      return throwError(
        () => new Error('batch modification requires an active plan context')
      );
    }
    if (uids.length === 0 || uids.length > MAX_BULK_PATCH_ITEMS) {
      return throwError(
        () =>
          new Error(
            `batch modification takes between 1 and ${MAX_BULK_PATCH_ITEMS} entities, got ${uids.length}`
          )
      );
    }

    const transformer = this.transformerFor(entityTypeVersion);
    if (!transformer) return EMPTY;

    const batches = chunk(uids, MAX_BATCH_PATCH_ITEMS);

    return defer(() => {
      let applied = 0;

      return from(batches).pipe(
        concatMap((batch) =>
          this.plansService
            .patchEntitiesBatch$(planId, entityType, {
              data: batch.map((uid) => ({ uid, ...fields })),
              transformer,
              entityTypeVersion
            })
            .pipe(map(() => (applied += batch.length)))
        ),
        // finalize rather than a completion handler: a batch that fails leaves the ones before
        // it written, so the views hold stale rows either way and still have to reload.
        finalize(() => {
          if (applied > 0) this.store.dispatch(entityModified({ entityType }));
        })
      );
    });
  }

  private transformerFor(entityTypeVersion: string): TransformerRequestVersion | undefined {
    const transformer = this.allTransformers().find((t) =>
      t.transformerInfo.dataModels.some(
        (dm) => dm.metadata.entityTypeVersion === entityTypeVersion
      )
    );
    if (!transformer) return undefined;

    return {
      name: transformer.transformerInfo.transformer.name,
      version: transformer.transformerInfo.transformer.version
    };
  }

  /**
   * Context menu modification path. Delegates to saveEntityData$.
   */
  applyModification(
    action: EntityModificationAction,
    option: EntityModificationOption
  ): Observable<void> {
    return this.saveEntityData$(
      action.entityType,
      action.entityTypeVersion,
      option.data
    ).pipe(
      tap(() => this.store.dispatch(entityModified({ entityType: action.entityType })))
    );
  }
}
